import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowTopRightOnSquareIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import PassagePanel from "../Passages/PassagePanel";
import { PreviewOption, RichText } from "../QuestionFolders/ImportPreviewQuestion";
import { DifficultyBadge } from "../Difficulty/Difficulty";
import { linkButtonClass, StatusBadge } from "../../pages/Exams/TestBuilder/ui";
import { hasImage, plainText, previewHtml } from "./variantUi";
import { isUnreadable } from "./variantText";

/**
 * In place of a question the user no longer reads (isUnreadable): "A question from another subject" and why its text
 * and options are not shown. `boxed`: a card of its own (a full preview); otherwise two lines (a place of the
 * composition).
 */
export function UnreadableNote({ boxed = false }) {
  const [t] = useTranslation();
  return (
    <div className={`flex items-start gap-2 ${boxed ? "rounded-xl border border-slate-200 bg-slate-50/60 p-4" : ""}`}>
      <EyeSlashIcon className={`shrink-0 text-slate-400 ${boxed ? "h-5 w-5" : "mt-0.5 h-4 w-4"}`} aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-800">{t("variants.unreadable.title")}</p>
        <p className={`text-slate-500 ${boxed ? "mt-0.5 text-sm" : "text-xs"}`}>{t("variants.unreadable.hint")}</p>
      </div>
    </div>
  );
}

// The start of a question's text in at most two lines, formulas rendered; "(image)" for a picture alone, "A question
// from another subject" for one the user no longer reads.
export function QuestionLine({ question, className = "" }) {
  const [t] = useTranslation();
  if (isUnreadable(question)) {
    return <span className={`line-clamp-2 italic text-slate-500 ${className}`}>{t("variants.unreadable.title")}</span>;
  }
  const body = question?.body;
  const text = plainText(body);
  return (
    <span className={`line-clamp-2 [overflow-wrap:anywhere] [&_.katex-display]:inline [&_.katex-display]:m-0 ${className}`}>
      {text ? <RichText html={previewHtml(body)} /> : hasImage(body) ? t("variants.slot.image_only") : question?.name || "—"}
    </span>
  );
}

// "№7": a question's number in the variant.
export function NumberBadge({ number, className = "" }) {
  const [t] = useTranslation();
  return (
    <span
      className={`inline-flex h-6 min-w-[2.25rem] shrink-0 items-center justify-center rounded-md bg-slate-900 px-1.5 text-xs font-semibold tabular-nums text-white ${className}`}
    >
      {t("variants.number", { number })}
    </span>
  );
}

/**
 * A full question (body, options with the correct one marked, its passage collapsed) as the bank's previews show
 * it: in the candidates dialog and when a place of the composition is opened. `number` is its number in the variant.
 * One the user no longer reads has its number and code only, and says why (its page and passage are out of reach).
 */
export default function QuestionContent({ question, number }) {
  const [t] = useTranslation();
  if (!question) return null;
  if (isUnreadable(question)) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {number != null && <NumberBadge number={number} />}
          {question.code && <span className="font-mono text-xs text-slate-500">{question.code}</span>}
        </div>
        <UnreadableNote boxed />
      </div>
    );
  }
  const topic = [question.subtopic?.topic?.name, question.subtopic?.name].filter(Boolean).join(" › ");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {number != null && <NumberBadge number={number} />}
        {question.code && <span className="font-mono text-xs text-slate-500">{question.code}</span>}
        <DifficultyBadge level={question.difficulty} />
        <StatusBadge status={question.status} />
        <a
          href={`/questions/${question.uuid}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${linkButtonClass} ml-auto text-xs`}
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          {t("variants.open_question")}
        </a>
      </div>
      {question.name && question.name !== question.code && <p className="text-sm font-medium text-slate-900">{question.name}</p>}
      {/* Keyed by the passage: stepping through its questions keeps the text open. */}
      {question.passage?.uuid && <PassagePanel key={question.passage.uuid} passage={question.passage} />}
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm leading-relaxed text-slate-800 [&_img]:max-h-80 [&_img]:w-auto">
        <RichText html={question.body} />
      </div>
      {question.answer_keys?.length > 0 && (
        <ul className="space-y-1.5" aria-label={t("variants.options")}>
          {question.answer_keys.map((option, i) => (
            <PreviewOption key={option.uuid || option.option || i} option={option} />
          ))}
        </ul>
      )}
      {topic && <p className="border-t border-slate-100 pt-2 text-xs text-slate-500">{topic}</p>}
    </div>
  );
}
